import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { cases } from '../data/mock';
import Footer from '../components/Footer';

const Work = () => {
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.15 });
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="min-h-screen w-full pb-32" style={{ background: 'var(--bg-dark)' }}>
      {/* Header band */}
      <section className="pt-40 pb-24 px-8 md:px-[90px]">
        <div className="font-label text-[0.7rem] text-[var(--terracotta)] mb-8">
          ————  Udvalgte arbejder  /  2021 — 2025
        </div>
        <h1 className="font-display text-[clamp(3rem,9vw,9rem)] text-[var(--cream)] leading-[0.92] max-w-5xl">
          Historier,<br />
          <span style={{ fontStyle: 'italic', fontWeight: 300 }}>komponeret.</span>
        </h1>
        <p className="font-body text-[var(--cream)]/55 max-w-xl mt-10 text-lg leading-relaxed">
          Et udvalg af arbejder inden for visuel identitet, editorial design og kampagner — hvert projekt formet af en enkel overbevisning: design er historiens første billede.
        </p>
      </section>

      {/* Cases list — staggered editorial grid */}
      <section className="px-8 md:px-[90px]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-32">
          {cases.map((c, i) => {
            const layouts = [
              { col: 'md:col-span-7 md:col-start-1', mt: '' },
              { col: 'md:col-span-6 md:col-start-7', mt: 'md:mt-32' },
              { col: 'md:col-span-7 md:col-start-2', mt: '' },
              { col: 'md:col-span-6 md:col-start-7', mt: 'md:mt-24' },
              { col: 'md:col-span-7 md:col-start-1', mt: '' },
              { col: 'md:col-span-6 md:col-start-7', mt: 'md:mt-32' }
            ];
            const lay = layouts[i % layouts.length];
            return (
              <Link
                key={c.id}
                to={`/work/${c.slug}`}
                className={`reveal ${lay.col} ${lay.mt} group block`}
                ref={(el) => (refs.current[i] = el)}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="relative overflow-hidden" style={{ aspectRatio: '4 / 5' }}>
                  <div
                    className="absolute inset-0 transition-transform duration-[1300ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                    style={{
                      backgroundImage: `url(${c.cover})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'brightness(0.88) contrast(1.04) saturate(0.95)'
                    }}
                  />
                  <div
                    className="absolute inset-0 transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                    style={{ background: `linear-gradient(180deg, ${c.accent}33 0%, ${c.accent}77 100%)` }}
                  />
                  <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                    <span
                      className="font-label text-[0.66rem] bg-[var(--bg-dark)]/55 px-2 py-1 backdrop-blur-sm border border-[var(--cream)]/10"
                      style={{ color: c.accent }}
                    >
                      {c.category}
                    </span>
                    <span className="font-label text-[0.66rem] text-[var(--cream)] bg-[var(--bg-dark)]/55 px-2 py-1 backdrop-blur-sm border border-[var(--cream)]/10">
                      {c.year}
                    </span>
                  </div>
                  <div className="absolute right-5 bottom-5 w-11 h-11 rounded-full bg-[var(--bg-dark)]/55 backdrop-blur-sm flex items-center justify-center transition-all duration-500 group-hover:bg-[var(--terracotta)] group-hover:rotate-45">
                    <ArrowUpRight size={18} className="text-[var(--cream)]" />
                  </div>
                </div>
                <div className="mt-6 flex items-baseline justify-between gap-6">
                  <div>
                    <div className="font-label text-[0.7rem] text-[var(--cream)]/50 mb-2">{c.title}</div>
                    <h3 className="font-display text-3xl md:text-4xl text-[var(--cream)] group-hover:text-[var(--terracotta)] transition-colors duration-500">
                      {c.subtitle}
                    </h3>
                  </div>
                  <span className="font-label text-[0.62rem] text-[var(--cream)]/40 hidden md:inline">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <p className="font-body text-[var(--cream)]/50 mt-3 max-w-md leading-relaxed">
                  {c.descriptor}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Work;
